import { X, Loader2, AlertTriangle, Trash2 } from "lucide-react";

const ROUTES = ["PO", "topical", "IM", "SC", "IV", "INH", "PR", "SL"];

/**
 * Modal for adding or editing a single medication for a client.
 * Stateless w.r.t. the form payload — parent owns `form` + `setForm`.
 * `editing === "new"` hides the delete button.
 */
export default function MedicationEditorModal({
  editing,
  form,
  setForm,
  saving,
  onClose,
  onSave,
  onDelete,
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border border-clinical-border w-full max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="med-editor-modal"
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">
            {editing === "new" ? "Add medication" : "Edit medication"}
          </h3>
          <button onClick={onClose} className="btn-ghost p-1.5" data-testid="med-modal-close" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Confirm with the client's care plan or prescriber before changing scheduled
              medications. All edits are linked to your caregiver account.
            </span>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">Medication name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Metformin"
              className="input-clinical"
              data-testid="med-form-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Dosage</label>
              <input
                value={form.dosage}
                onChange={(e) => setForm({ ...form, dosage: e.target.value })}
                placeholder="e.g. 500 mg"
                className="input-clinical"
                data-testid="med-form-dosage"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Route</label>
              <select
                value={form.route}
                onChange={(e) => setForm({ ...form, route: e.target.value })}
                className="input-clinical"
                data-testid="med-form-route"
              >
                {ROUTES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">Scheduled times</label>
            <input
              value={form.scheduled_times}
              onChange={(e) => setForm({ ...form, scheduled_times: e.target.value })}
              placeholder="08:00, 20:00"
              className="input-clinical font-mono"
              disabled={form.is_prn}
              data-testid="med-form-times"
            />
            <p className="mt-1 text-[11px] text-clinical-subtext">
              Comma-separated 24-hour times. Leave blank and mark PRN below for as-needed
              medications.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_prn}
              onChange={(e) => setForm({ ...form, is_prn: e.target.checked })}
              className="accent-clinical-primary"
              data-testid="med-form-prn"
            />
            <span className="text-clinical-text">PRN — as needed</span>
          </label>

          <div>
            <label className="label-eyebrow mb-1.5 block">Instructions (optional)</label>
            <textarea
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              rows={2}
              placeholder="e.g. Take with food. Hold if BP &lt; 100/60."
              className="input-clinical resize-y"
              data-testid="med-form-instructions"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-between gap-2 px-5 py-4 border-t border-clinical-border">
          <div>
            {editing !== "new" && (
              <button
                onClick={onDelete}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
                data-testid="med-form-delete"
              >
                <Trash2 className="h-4 w-4" /> Remove
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="med-form-cancel">
              Cancel
            </button>
            <button onClick={onSave} disabled={saving} className="btn-primary" data-testid="med-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
